import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { hash } from '@/utils/hash';
import useRegister from '../hooks/useRegister';

export function Register() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { register, registerLoading } = useRegister();

	const registrationSchema = z.object({
		firstname: z.string().min(1, { message: t('register.error.required') }),
		lastname: z.string().min(1, { message: t('register.error.required') }),
		email: z.string().email({ message: t('register.error.email') }),
		password: z.string().min(8, { message: t('register.error.password') }),
	});

	const form = useForm<z.infer<typeof registrationSchema>>({
		resolver: zodResolver(registrationSchema),
		defaultValues: {
			firstname: '',
			lastname: '',
			email: '',
			password: '',
		},
	});

	const onSubmit = (values: z.infer<typeof registrationSchema>) => {
		values.password = hash(values.password);
		register(values, {
			onSuccess() {
				navigate('/validate', {
					state: { email: values.email, type: 'register' },
				});
			},
		});
	};

	return (
		<Card className="mx-auto -mt-9 max-w-sm self-center">
			<CardHeader>
				<CardTitle className="text-xl">{t('register.title')}</CardTitle>
				<CardDescription>{t('register.description')}</CardDescription>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="grid gap-4"
					>
						<div className="grid grid-cols-2 gap-4">
							<div className="grid gap-2">
								<FormField
									control={form.control}
									name="firstname"
									render={({ field }) => (
										<FormItem>
											<Input
												id="firstname"
												label={t(
													'register.input.firstname.label'
												)}
												autoComplete="given-name"
												placeholder={t(
													'register.input.firstname.placeholder'
												)}
												{...field}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="grid gap-2">
								<FormField
									control={form.control}
									name="lastname"
									render={({ field }) => (
										<FormItem>
											<Input
												id="lastname"
												label={t(
													'register.input.lastname.label'
												)}
												autoComplete="family-name"
												placeholder={t(
													'register.input.lastname.placeholder'
												)}
												{...field}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<div className="grid gap-2">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<Input
											id="email"
											label={t(
												'register.input.email.label'
											)}
											type="email"
											placeholder={t(
												'register.input.email.placeholder'
											)}
											{...field}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="grid gap-2">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<Input
											id="password"
											label={t(
												'register.input.password.label'
											)}
											type="password"
											{...field}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<Button
							type="submit"
							className="w-full"
							isLoading={registerLoading}
						>
							{t('register.button.register')}
						</Button>
						{/* <Button variant="outline" className="w-full">
						{t('register.button.registerGoogle')}
					</Button> */}
					</form>
					<div className="mt-4 text-center text-sm">
						{t('register.login.question')}
						<Link to="/" className="ml-2 underline">
							{t('register.login.link')}
						</Link>
					</div>
				</Form>
			</CardContent>
		</Card>
	);
}
